var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FlexBox, FormField, FormGrid, Icon, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { conversationsApi } from 'apis/conversations';
import { userContentApi } from 'apis/userContent';
import { Dropzone } from 'components/dropzone';
import { HookFormDatePicker, HookFormPhoneNumberSelect, HookFormQueueSelect, HookFormText, } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getAsyncStatusFromRtkMutationStates } from 'utils/apis/axios';
import { createAppModal } from 'utils/modal';
import { ScheduledConversationsBanner } from '../banner/scheduledConversationsBanner';
import styles from './scheduledConversationsModal.module.scss';
export const ScheduledConversationsModal = createAppModal({ type: 'settings:scheduledConversations' }, ({ isOpen, hide }) => {
    var _a, _b, _c, _d, _e;
    const notify = useNotifications();
    const form = useForm({ mode: 'all' });
    const [uploadCsv, uploadCsvState] = userContentApi.useCreateScheduledConversationsCsvMutation();
    const [createScheduledConversationBatchQuery, createScheduledConversationBatchState,] = conversationsApi.useLazyCreateScheduledConversationBatchQuery();
    // Prefills the name field with the filename of the uploaded file, if the field is empty.
    useEffect(() => {
        var _a;
        const { status, originalArgs } = uploadCsvState;
        const values = form.getValues();
        if (status === 'fulfilled' && !values.name) {
            form.setValue('name', (_a = originalArgs === null || originalArgs === void 0 ? void 0 : originalArgs.file.name) !== null && _a !== void 0 ? _a : '');
            form.clearErrors('name');
        }
    }, [form, uploadCsvState]);
    const handleSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        var _f;
        try {
            yield createScheduledConversationBatchQuery(Object.assign(Object.assign({}, values), { outboundNumber: values.outboundNumber[0].e164_format, date: (_f = values.date.date) !== null && _f !== void 0 ? _f : moment().toISOString() })).unwrap();
            notify({
                title: 'Success',
                text: `The conversations have been scheduled.`,
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            hide === null || hide === void 0 ? void 0 : hide();
        }
        catch (error) {
            notify({
                title: 'Error while scheduling the conversations.',
            }, NotificationType.Error);
        }
    });
    const handleUpload = (files) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const { data: { relative_uri }, } = yield uploadCsv({ file: files[0] }).unwrap();
            form.setValue('csvPath', relative_uri);
        }
        catch (_g) {
            /**
             * No need to do anything, any error from the query is rendered in the
             * UI.
             */
        }
    });
    const fileName = (_c = (_b = (_a = uploadCsvState.originalArgs) === null || _a === void 0 ? void 0 : _a.file) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '';
    const asyncStatusDescriptions = {
        uninitialized: 'Drag and drop a .csv file here or click to upload',
        pending: `Uploading...`,
        fulfilled: `"${fileName}" uploaded successfully`,
        rejected: `"${fileName}" has the following errors:`,
    };
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: hide, className: styles.modal }, { children: [_jsx(ModalHead, { title: "Import .csv and add scheduled conversations", onClose: hide }), _jsx(ModalBody, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "400" }, { children: [_jsx(ScheduledConversationsBanner, {}), _jsxs("div", Object.assign({ className: styles.grid }, { children: [_jsx(Dropzone, Object.assign({ accept: ".csv", onDrop: handleUpload, status: uploadCsvState.status }, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", alignItems: "center", gap: "300" }, { children: [_jsx(Icon, { name: "upload", color: "", size: 24 }), _jsx(Text.Paragraph, Object.assign({ variant: "3", subdued: true, align: "center" }, { children: asyncStatusDescriptions[uploadCsvState.status] })), uploadCsvState.error && (_jsx(FlexBox, Object.assign({ flexDirection: "column" }, { children: (_e = (_d = uploadCsvState.error.data) === null || _d === void 0 ? void 0 : _d.errors) === null || _e === void 0 ? void 0 : _e.map((e, i) => (_jsx(Text.Paragraph, Object.assign({ variant: "4", subdued: true }, { children: e }), i))) })))] })) })), _jsx("form", Object.assign({ noValidate: true, tabIndex: -1 }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, { children: _jsx(HookFormText, { name: "name", label: "Schedule name", control: form.control, rules: {
                                                        required: 'Schedule name is required',
                                                    }, componentProps: {
                                                        autoFocus: true,
                                                        placeholder: 'Enter the schedule name',
                                                    } }) }), _jsx(FormField, { children: _jsx(HookFormQueueSelect, { name: "queueId", label: "Conversations offered in...", control: form.control, rules: {
                                                        required: 'Queue is required',
                                                    }, componentProps: {
                                                        infiniteScroll: false,
                                                    } }) }), _jsx(FormField, { children: _jsx(HookFormPhoneNumberSelect, { name: "outboundNumber", label: "Conversations offered from...", control: form.control, rules: {
                                                        required: 'Phone number is required',
                                                    } }) }), _jsx(FormField, { children: _jsx(HookFormDatePicker, { name: "date", label: "Conversations offered on...", control: form.control, rules: {
                                                        required: 'Date is required',
                                                    }, componentProps: {
                                                        placeholder: 'Select a date and time',
                                                        dateFormat: moment.localeData().longDateFormat('LLL'),
                                                        datePickerProps: {
                                                            withTimePicker: true,
                                                        },
                                                        minDate: moment().subtract(1, 'day'),
                                                    } }) })] }) }))] }))] })) }), _jsx(ModalFoot, { cancelOnClick: hide, confirmName: "Upload", confirmOnClick: form.handleSubmit(handleSubmit), confirmProps: {
                    isLoading: createScheduledConversationBatchState.isLoading,
                    disabled: !form.formState.isValid ||
                        getAsyncStatusFromRtkMutationStates(uploadCsvState) !==
                            'fulfilled' ||
                        form.formState.isSubmitting,
                } })] })));
});
